// Recurring Invoice — scheduler, email and numbering settings for the branch.
import React, { useEffect, useState } from "react";
import { Btn, IC, SectHead } from "./RecurringParts";

export const DEFAULT_SETTINGS = {
  runTime: "06:00",
  raiseAhead: 0,
  onFailure: "RetryAndAlert",
  createAsDraft: false,
  skipOverCreditLimit: true,
  stopAfterEndDate: true,
  emailFrom: "",
  emailBcc: "",
  emailTemplate: "English",
  attachPdf: true,
  reminderBeforeDue: true,
  reminderBeforeDays: 3,
  overdueReminders: true,
  overdueReminderDays: "7,15,30",
  profilePrefix: "RP-",
  invoicePrefix: "RI-",
  seriesMode: "OwnSeries",
};

export const toSettings = (s) => ({ ...DEFAULT_SETTINGS, ...(s && typeof s === "object" ? s : {}) });

const Sw = ({ checked, onChange, label }) => (
  <label className="axrc-switch">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
    <span className="track" />
    <span>{label}</span>
  </label>
);

const Sel = ({ id, value, onChange, options }) => (
  <>
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => (
        <option key={o.v} value={o.v}>
          {o.l}
        </option>
      ))}
    </select>
    {IC.caret}
  </>
);

const RecurringSettings = ({ settings, onSave, permit, saving, loaded }) => {
  const [s, setS] = useState(settings);
  useEffect(() => setS(settings), [settings]);
  const set = (k) => (v) => setS((x) => ({ ...x, [k]: v }));
  const can = permit.edit;
  return (
    <section className="axrc-screen">
      <div className="axrc-phead">
        <div>
          <h1>Schedule &amp; email settings</h1>
          <div className="sub">How the module behaves when nobody is watching</div>
        </div>
        <div className="sp" />
        {can && (
          <Btn kind="primary" icon={IC.check} onClick={() => onSave(s)} disabled={saving}>
            Save settings
          </Btn>
        )}
      </div>
      <div className="axrc-card axrc-settings">
        <div className="axrc-cbody">
          {!loaded && <div className="axrc-callout info" style={{ marginTop: 0, marginBottom: 12 }}>These are the defaults — the server has not sent this branch&apos;s settings.</div>}
          <fieldset disabled={!can}>
            <SectHead>Scheduler</SectHead>
            <div className="axrc-formgrid three">
              <div className="axrc-f">
                <label htmlFor="rcSTime">Run every day at</label>
                <input type="time" id="rcSTime" value={s.runTime} onChange={(e) => set("runTime")(e.target.value)} />
              </div>
              <div className="axrc-f sel">
                <label htmlFor="rcSAhead">Raise the invoice</label>
                <Sel
                  id="rcSAhead"
                  value={s.raiseAhead}
                  onChange={(v) => set("raiseAhead")(+v)}
                  options={[
                    { v: 0, l: "On the due date" },
                    { v: 1, l: "1 day before" },
                    { v: 3, l: "3 days before" },
                    { v: 7, l: "7 days before" },
                  ]}
                />
              </div>
              <div className="axrc-f sel">
                <label htmlFor="rcSFail">If generation fails</label>
                <Sel
                  id="rcSFail"
                  value={s.onFailure}
                  onChange={set("onFailure")}
                  options={[
                    { v: "RetryAndAlert", l: "Retry next run and alert accounts" },
                    { v: "RetryThenPause", l: "Retry three times, then pause the profile" },
                    { v: "AlertOnly", l: "Alert only" },
                  ]}
                />
              </div>
              <div className="axrc-f">
                <label>Create as</label>
                <Sw checked={s.createAsDraft} onChange={set("createAsDraft")} label="Draft, needs approval" />
              </div>
              <div className="axrc-f">
                <label>Skip when the customer is over the credit limit</label>
                <Sw checked={s.skipOverCreditLimit} onChange={set("skipOverCreditLimit")} label={s.skipOverCreditLimit ? "On" : "Off"} />
              </div>
              <div className="axrc-f">
                <label>Stop when the end date passes</label>
                <Sw checked={s.stopAfterEndDate} onChange={set("stopAfterEndDate")} label={s.stopAfterEndDate ? "On" : "Off"} />
              </div>
            </div>

            <SectHead>Email</SectHead>
            <div className="axrc-formgrid three">
              <div className="axrc-f">
                <label htmlFor="rcSFrom">Send from</label>
                <input id="rcSFrom" value={s.emailFrom} placeholder="accounts@yourcompany.com" onChange={(e) => set("emailFrom")(e.target.value)} />
              </div>
              <div className="axrc-f">
                <label htmlFor="rcSBcc">Always BCC</label>
                <input id="rcSBcc" value={s.emailBcc} placeholder="Optional" onChange={(e) => set("emailBcc")(e.target.value)} />
              </div>
              <div className="axrc-f sel">
                <label htmlFor="rcSTpl">Template</label>
                <Sel
                  id="rcSTpl"
                  value={s.emailTemplate}
                  onChange={set("emailTemplate")}
                  options={[
                    { v: "English", l: "Recurring invoice — English" },
                    { v: "Arabic", l: "Recurring invoice — Arabic" },
                    { v: "Bilingual", l: "Recurring invoice — bilingual" },
                  ]}
                />
              </div>
              <div className="axrc-f">
                <label>Attach the PDF</label>
                <Sw checked={s.attachPdf} onChange={set("attachPdf")} label={s.attachPdf ? "On" : "Off"} />
              </div>
              <div className="axrc-f">
                <label>Payment reminder before the due date</label>
                <Sw checked={s.reminderBeforeDue} onChange={set("reminderBeforeDue")} label={s.reminderBeforeDays + " days before"} />
              </div>
              <div className="axrc-f">
                <label>Overdue reminder</label>
                <Sw checked={s.overdueReminders} onChange={set("overdueReminders")} label={String(s.overdueReminderDays).split(",").join(", ") + " days after"} />
              </div>
            </div>

            <SectHead>Numbering</SectHead>
            <div className="axrc-formgrid three">
              <div className="axrc-f">
                <label htmlFor="rcSPP">Profile prefix</label>
                <input id="rcSPP" className="mono" value={s.profilePrefix} onChange={(e) => set("profilePrefix")(e.target.value)} />
              </div>
              <div className="axrc-f">
                <label htmlFor="rcSIP">Invoice prefix</label>
                <input id="rcSIP" className="mono" value={s.invoicePrefix} onChange={(e) => set("invoicePrefix")(e.target.value)} />
              </div>
              <div className="axrc-f sel">
                <label htmlFor="rcSSeries">Series</label>
                <Sel
                  id="rcSSeries"
                  value={s.seriesMode}
                  onChange={set("seriesMode")}
                  options={[
                    { v: "SalesSeries", l: "Continue the sales invoice series" },
                    { v: "OwnSeries", l: "Keep its own series" },
                  ]}
                />
              </div>
            </div>
          </fieldset>
        </div>
      </div>
    </section>
  );
};

export default RecurringSettings;
